/* global Theme */
import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney, getGiftFunnel } from '@bbd/gwp-funnel';
import {
  GIFT_PROPERTY,
  GIFT_PROPERTY_VALUE,
  GIFT_REMOVED_KEY as REMOVED_KEY,
  cartSignature,
  giftToKeep,
  isGiftLine,
  isGiftLocked
} from '@bbd/cart-rules';

/** The customer chose "Geen", used by the open-section controller (M7) */
const DISMISSED_KEY = 'bbd_gwp_dismissed';
/** Predictive search returns at most 10 products per request */
const PREDICTIVE_SEARCH_LIMIT = 10;
/** Dispatched on document after every sync, for the open-section controller (assets/bbd-cart-sections.js) */
const GIFT_SYNCED_EVENT = 'bbd-gift:synced';
/** Dispatched by assets/bbd-quick-add-gift.js when the modal starts adding a gift, detail: { busy: boolean } */
const GIFT_BUSY_EVENT = 'bbd-gift:busy';

let funnelUnavailable = false;

/**
 * @param {string} key
 * @param {string | null} value - null removes the key
 */
const writeStorage = (key, value) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage unavailable, the state just won't persist
  }
};

/**
 * @typedef {{ key: string, id: number, product_id: number, final_line_price: number, properties: Record<string, string> | null }} CartLine
 * @typedef {{ items: CartLine[], items_subtotal_price: number, item_count: number, sections?: Record<string, string> }} Cart
 */

class BbdGiftPicker extends HTMLElement {
  /** @type {number} Threshold in cents, from the Monk funnel (assets/bbd-gwp-funnel.js) */
  #threshold = 0;

  /** Whether the funnel is loaded and the tiles are rendered: syncing before that would work with a threshold of 0 */
  #ready = false;

  /** @type {Set<string>} Line keys of the gifts we saw last, to find out which one was added last */
  #knownGiftKeys = new Set();

  #syncing = false;
  #syncAgain = false;
  #busy = false;

  /** @type {HTMLElement | null} */
  #row = null;

  /** @type {Element | null} The tile that was selected last, the row scrolls to it when that changes */
  #selectedTile = null;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(GIFT_BUSY_EVENT, this.#onBusyEvent);
    this.closest('details')?.addEventListener('toggle', this.#onToggle);
    this.#init();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(GIFT_BUSY_EVENT, this.#onBusyEvent);
    this.closest('details')?.removeEventListener('toggle', this.#onToggle);
  }

  /**
   * The row cannot be scrolled while the section is closed, so it scrolls to the selected tile when it opens.
   * Without that the row can show the products further on and the selected tile is out of sight.
   */
  #onToggle = (/** @type {Event} */ event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.open) this.#scrollToSelected('auto');
  };

  /**
   * Makes sure the selected tile is in view.
   * @param {ScrollBehavior} behavior
   */
  #scrollToSelected(behavior) {
    const row = this.#row;
    const tile = row?.querySelector('.bbd-cart-tile--selected');
    if (!row || !(tile instanceof HTMLElement)) return;

    const inView =
      tile.offsetLeft >= row.scrollLeft && tile.offsetLeft + tile.offsetWidth <= row.scrollLeft + row.clientWidth;
    if (!inView) row.scrollTo({ left: tile.offsetLeft, behavior });
  }

  async #init() {
    try {
      if (funnelUnavailable) throw new Error('Monk funnel unavailable');

      const { threshold, productIds } = await getGiftFunnel(this.dataset.shopUrl ?? '');
      this.#threshold = threshold;

      const tiles = await this.#loadTiles(productIds);
      this.#render(tiles);
      this.#ready = true;
    } catch (error) {
      funnelUnavailable = true;
      console.error('BBD gift picker, falling back to the Monk widget:', error);
      this.#fallback();
      return;
    }

    await this.#sync();
  }

  /**
   * Shows the Monk widget mount instead of the picker.
   */
  #fallback() {
    const fallback = this.parentElement?.querySelector('[data-gift-fallback]');
    if (fallback instanceof HTMLElement) fallback.hidden = false;
    this.remove();
  }

  /**
   * Tile elements for the gifts Monk offers, "Geen" first.
   * Monk decides which products are offered and in which order. Shopify supplies the product data: first from the
   * shop metafield (works for unlisted products), then through predictive search (listed products only).
   * @param {number[]} productIds
   * @returns {Promise<Element[]>}
   */
  async #loadTiles(productIds) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) throw new Error('Section ID is required for rendering');

    /** @type {Map<number, Element>} */
    const tilesByProductId = new Map();
    /** @type {Element | null} */
    let noneTile = null;

    const collect = (/** @type {string} */ html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      for (const tile of doc.querySelectorAll('[data-gift-tile-none], [data-gift-tile]')) {
        if (tile.hasAttribute('data-gift-tile-none')) noneTile ??= tile;
        else tilesByProductId.set(Number(tile.getAttribute('data-product-id')), tile);
      }
    };

    collect(await sectionRenderer.getSectionHTML(sectionId, true));

    const missing = productIds.filter((id) => !tilesByProductId.has(id));
    for (let index = 0; index < missing.length; index += PREDICTIVE_SEARCH_LIMIT) {
      const chunk = missing.slice(index, index + PREDICTIVE_SEARCH_LIMIT);
      const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
      url.searchParams.set('q', chunk.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(chunk.length));

      collect(await sectionRenderer.getSectionHTML(sectionId, true, url));
    }

    const notFound = productIds.filter((id) => !tilesByProductId.has(id));
    if (notFound.length > 0) {
      console.warn(
        'BBD gift picker: Monk offers products that Shopify did not return, add them to the metafield custom.gift_with_purchase_products:',
        notFound
      );
    }

    const productTiles = productIds.map((id) => tilesByProductId.get(id)).filter((tile) => tile !== undefined);
    if (!noneTile || productTiles.length === 0) throw new Error('No gift products available');

    return [noneTile, ...productTiles];
  }

  /**
   * @param {Element[]} tiles
   */
  #render(tiles) {
    const wrapper = document.createElement('div');
    wrapper.className = 'bbd-cart-tiles';

    const row = document.createElement('div');
    row.className = 'bbd-cart-tiles__row';
    row.append(...tiles);

    const previous = this.#createArrow('previous');
    const next = this.#createArrow('next');
    wrapper.append(previous, row, next);

    // One line under the tiles, its text depends on whether the threshold is reached (set in #updateTiles)
    const note = document.createElement('p');
    note.className = 'bbd-gift-picker__note';
    note.dataset.giftNote = '';
    note.hidden = true;

    this.replaceChildren(wrapper, note);
    this.#row = row;

    const updateArrows = () => {
      previous.hidden = row.scrollLeft <= 1;
      next.hidden = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
    };
    row.addEventListener('scroll', updateArrows, { passive: true });
    new ResizeObserver(updateArrows).observe(row);
    updateArrows();

    const scroll = (/** @type {number} */ direction) => {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      row.scrollBy({ left: direction * row.clientWidth * 0.8, behavior });
    };
    previous.addEventListener('click', () => scroll(-1));
    next.addEventListener('click', () => scroll(1));

    this.addEventListener('click', this.#onClick);
  }

  /**
   * @param {'previous' | 'next'} direction
   * @returns {HTMLButtonElement}
   */
  #createArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `bbd-cart-tiles__arrow bbd-cart-tiles__arrow--${direction}`;
    button.setAttribute(
      'aria-label',
      (direction === 'previous' ? this.dataset.labelPrevious : this.dataset.labelNext) ?? ''
    );
    button.hidden = true;

    const icon = document.querySelector(`template[data-gift-arrow="${direction}"]`);
    if (icon instanceof HTMLTemplateElement) button.append(icon.content.cloneNode(true));

    return button;
  }

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const none = event.target.closest('[data-gift-none]');
    if (none) {
      this.#chooseNone();
      return;
    }

    const add = event.target.closest('[data-gift-add]');
    if (add instanceof HTMLElement && add.dataset.variantId) {
      this.#addGift(Number(add.dataset.variantId));
      return;
    }

    // A tap on the tile itself does what its button does. The button stays the control for keyboard and screen readers.
    const tile = event.target.closest('[data-gift-tile], [data-gift-tile-none]');
    if (!tile || event.target.closest('button, a')) return;

    const button = tile.querySelector('button:not([hidden]):not(:disabled)');
    if (button instanceof HTMLButtonElement) button.click();
  };

  /**
   * @param {CartUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    // Most updates carry the whole cart, a product form add or a single line does not
    const resource = event.detail?.resource;
    this.#sync(Array.isArray(resource?.items) ? resource : undefined);
  };

  /**
   * @param {CustomEvent<{ busy: boolean }>} event
   */
  #onBusyEvent = (event) => {
    this.#setBusy(event.detail.busy);
  };

  /**
   * Shows that a gift is being added or removed: a veil with a spinner over the row, nothing can be tapped meanwhile.
   * @param {boolean} busy
   */
  #setBusy(busy) {
    if (busy) this.dataset.busy = 'true';
    else delete this.dataset.busy;

    this.setAttribute('aria-busy', String(busy));
  }

  /**
   * Asks Shopify to render the cart sections in the same request, like the product form does. Without it the
   * cart view needs a second round trip after the gift was added.
   * @returns {{ sections: string, sections_url: string }}
   */
  #sectionsParams() {
    const ids = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : undefined))
      .filter(Boolean);

    return { sections: [...new Set(ids)].join(','), sections_url: window.location.pathname };
  }

  /**
   * @returns {Promise<Cart>}
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
    return response.json();
  }

  /**
   * Brings the tiles in line with the cart, and enforces the gift rules: one gift at most, never below the threshold.
   * @param {Cart} [knownCart] - The cart of the update event, saves a request
   */
  async #sync(knownCart) {
    if (!this.#ready) return;

    if (this.#syncing) {
      this.#syncAgain = true;
      return;
    }
    this.#syncing = true;

    try {
      const cart = knownCart ?? (await this.#fetchCart());
      let gifts = cart.items.filter(isGiftLine);
      const locked = isGiftLocked(cart.items, this.#threshold);

      // Below the threshold the gift is no longer free: remove it and remember that for the progress line
      if (gifts.length > 0 && locked) {
        writeStorage(REMOVED_KEY, '1');
        await this.#removeLines(gifts);
        return;
      }

      // Never more than one gift. Keep the one that was added last (a key we have not seen before)
      if (gifts.length > 1) {
        const keep = giftToKeep(gifts, this.#knownGiftKeys);
        const surplus = gifts.filter((gift) => gift !== keep);
        this.#knownGiftKeys = new Set([keep?.key]);
        await this.#removeLines(surplus);
        return;
      }

      this.#knownGiftKeys = new Set(gifts.map((gift) => gift.key));
      gifts = cart.items.filter(isGiftLine);
      if (gifts.length > 0) writeStorage(DISMISSED_KEY, null);
      if (!locked) writeStorage(REMOVED_KEY, null);

      this.#updateTiles(gifts[0] ?? null, locked);

      document.dispatchEvent(
        new CustomEvent(GIFT_SYNCED_EVENT, {
          detail: {
            locked,
            giftKey: gifts[0]?.key ?? null,
            giftTitle: gifts[0]?.product_title ?? '',
            items: cart.items,
            cartSignature: cartSignature(cart.items)
          }
        })
      );
    } catch (error) {
      console.error('BBD gift picker sync failed:', error);
    } finally {
      this.#syncing = false;
      if (!this.#busy) this.#setBusy(false);
      if (this.#syncAgain) {
        this.#syncAgain = false;
        this.#sync();
      }
    }
  }

  /**
   * The note under the tiles while the threshold is not reached, with the threshold of the funnel in it.
   * @returns {string}
   */
  #lockedNote() {
    const format = this.dataset.moneyFormat;
    if (!this.dataset.noteLocked || !format) return '';

    return this.dataset.noteLocked.replace('[amount]', formatMoney(this.#threshold, format));
  }

  /**
   * @param {CartLine | null} gift
   * @param {boolean} locked
   */
  #updateTiles(gift, locked) {
    this.dataset.locked = String(locked);

    const note = this.querySelector('[data-gift-note]');
    if (note instanceof HTMLElement) {
      const text = locked ? this.#lockedNote() : (this.dataset.note ?? '');
      if (note.textContent !== text) note.textContent = text;
      note.hidden = !text;
    }

    for (const tile of this.querySelectorAll('[data-gift-tile], [data-gift-tile-none]')) {
      const isNone = tile.hasAttribute('data-gift-tile-none');
      const isChosen = isNone ? gift === null : String(gift?.product_id) === tile.getAttribute('data-product-id');

      tile.classList.toggle('bbd-cart-tile--selected', isChosen);

      const status = tile.querySelector('.bbd-cart-tile__status');
      if (status instanceof HTMLElement) status.hidden = !isChosen;

      for (const button of tile.querySelectorAll('button')) {
        button.hidden = isChosen;
        button.disabled = locked && !isNone;
      }
    }

    const selected = this.querySelector('.bbd-cart-tile--selected');
    if (selected !== this.#selectedTile) {
      this.#selectedTile = selected;
      this.#scrollToSelected(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
    }
  }

  /**
   * Removes the lines and refreshes the cart view once, with the sections of the last request.
   * @param {CartLine[]} lines
   */
  async #removeLines(lines) {
    /** @type {Record<string, string> | undefined} */
    let sections;
    /** @type {Cart | undefined} */
    let cart;

    for (const [index, line] of lines.entries()) {
      const isLast = index === lines.length - 1;
      const body = { id: line.key, quantity: 0, ...(isLast ? this.#sectionsParams() : {}) };
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(body) }));

      if (isLast) {
        cart = await response.json();
        sections = cart?.sections;
      }
    }

    await this.#dispatchCartUpdate(sections, cart);
  }

  /**
   * @param {Record<string, string>} [sections] - Rendered cart sections from the last request, saves a round trip
   * @param {Cart} [knownCart]
   */
  async #dispatchCartUpdate(sections, knownCart) {
    const cart = knownCart?.items ? knownCart : await this.#fetchCart();
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.id || 'bbd-gift-picker', {
        source: 'bbd-gift-picker',
        itemCount: cart.item_count,
        sections
      })
    );
  }

  /**
   * Removes the gift, the customer chose "Geen".
   */
  async #chooseNone() {
    if (this.#busy) return;
    this.#busy = true;
    this.#setBusy(true);

    try {
      const cart = await this.#fetchCart();
      writeStorage(DISMISSED_KEY, '1');
      writeStorage(REMOVED_KEY, null);
      await this.#removeLines(cart.items.filter(isGiftLine));
    } finally {
      this.#busy = false;
      this.#setBusy(false);
    }
  }

  /**
   * Adds a single variant gift. Multi variant gifts go through the quick-add modal (assets/bbd-quick-add-gift.js).
   * @param {number} variantId
   */
  async #addGift(variantId) {
    if (this.#busy || this.dataset.locked === 'true') return;
    this.#busy = true;
    this.#setBusy(true);

    try {
      const cart = await this.#fetchCart();
      for (const gift of cart.items.filter(isGiftLine)) {
        await fetch(
          `${Theme.routes.cart_change_url}`,
          fetchConfig('json', { body: JSON.stringify({ id: gift.key, quantity: 0 }) })
        );
      }

      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE } }],
            ...this.#sectionsParams()
          })
        })
      );
      const result = await response.json();
      if (result.status) throw new Error(result.description ?? result.message);

      await this.#dispatchCartUpdate(result.sections);
    } catch (error) {
      console.error('BBD gift picker could not add the gift:', error);
    } finally {
      this.#busy = false;
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('bbd-gift-picker')) {
  customElements.define('bbd-gift-picker', BbdGiftPicker);
}
